import React from 'react';
import {
  IconSearch,
  PopoverTrigger,
  ButtonIcon,
  IconChevronDown,
  IconChevronUp,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverNavigationLink,
  Backdrop,
} from '@spotify-internal/encore-web';
import { sendEvent } from '../../Tracking';
import { RequireFeatureFlag } from '../../Auth';
import { useCurrentUser } from '../../../@mrkt/useCurrentUser';
import { useLayoutContext } from '../LayoutState';
import { UserState } from './types';
import { Link, ResponsiveLogo, Modal } from '../../ui';
import { useScrollYPosition } from '../../utils';
import { NavSideMenu } from './NavSideMenu';
import { OpenSideMenuButton } from './NavSideMenu.styles';
import {
  NavBarWrapper,
  LogoArea,
  LinksArea,
  ActionsArea,
  MenuButtonArea,
  LoginButton,
  ClaimButton,
  MenuLink,
  MenuCategory,
} from './NavBar.styles';
import { USE_SEARCH } from './constants';

type MenuState = {
  resources?: boolean;
  help?: boolean;
};

type NavBarProps = {
  forceState?: UserState;
  defaultMenuState?: MenuState;
};

const KEY_ESC = 27;
const KEY_SPACE = 32;
const SIDE_MENU_TRIGGER_BREAKPOINT = 992;
const TRACK_CAT = 'Top Nav';
const MENU_CLOSE_DELAY = 500;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MenuCategoryLink = React.forwardRef(({ children, ...props }, _) => (
  <PopoverNavigationLink {...props}>{children}</PopoverNavigationLink>
));

const menuTimeouts: { [key: string]: NodeJS.Timeout } = {};

export const NavBar: React.FC<NavBarProps> = ({
  forceState = null,
  defaultMenuState = {},
}) => {
  const layoutContext = useLayoutContext();
  const { navBarBackground, navBarTextColor, navBarBleed } = layoutContext;
  const user = useCurrentUser();
  const [userState, setUserState] = React.useState<UserState>('loading');
  const [menuState, setMenuState] = React.useState<MenuState>(defaultMenuState);
  const [sideMenuOpen, setSideMenuOpened] = React.useState(false);
  const scrollPos = useScrollYPosition();
  const mounted = React.useRef(true);

  const openSideMenu = React.useCallback(() => {
    document.body.classList.add('sidepanel-active');
    window.addEventListener('keydown', handleKey, true); // eslint-disable-line no-use-before-define
    window.addEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Open Menu - side',
    });
    setSideMenuOpened(true);
  }, []);

  const closeSideMenu = React.useCallback(() => {
    document.body.classList.remove('sidepanel-active');
    window.removeEventListener('keydown', handleKey, false); // eslint-disable-line no-use-before-define
    window.removeEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Close Menu - side',
    });
    setSideMenuOpened(false);
  }, []);

  const openMenu = (key: keyof MenuState) => {
    setMenuState({ [key]: true }); // closes all other menus
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Open Menu - ${key}`,
    });
  };

  const closeMenu = (key: keyof MenuState) => {
    if (!mounted.current) return;

    setMenuState(curState => ({ ...curState, [key]: false }));
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Close Menu - ${key}`,
    });
  };

  // event handlers for dropdown menus
  const getMenuHandlers = (key: keyof MenuState) => {
    return {
      onMouseEnter: () => {
        // open menu and clear timeouts
        const to = menuTimeouts[key];
        if (to) {
          clearTimeout(to);
          delete menuTimeouts[key];
        }
        openMenu(key);
      },
      onMouseLeave: () => {
        // close menu on delay for usability
        menuTimeouts[key] = setTimeout(() => closeMenu(key), MENU_CLOSE_DELAY);
      },
      onKeyPress: (e: any) => {
        if (e.which === KEY_SPACE) {
          if (menuState[key]) {
            closeMenu(key);
          } else {
            openMenu(key);
          }
        }
      },
      onClick: () => {
        if (!menuState[key]) {
          openMenu(key);
        }
      },
    };
  };

  React.useEffect(() => {
    return () => {
      document.body.classList.remove('sidepanel-active');
      mounted.current = false;
    };
  }, []);

  // set state of nav bar based on current user, or use 'forceState' if specified
  React.useEffect(() => {
    if (forceState) {
      setUserState(forceState);
      return;
    }
    setUserState(
      user
        ? (user.hasAccess && 'claimed') || 'unclaimed'
        : (user === undefined && 'loading') || 'loggedOut',
    );
  }, [user]);

  const handleKey = React.useCallback(e => {
    if (e.which === KEY_ESC) {
      closeSideMenu();
    }
  }, []);

  const handleResize = React.useCallback(() => {
    if (window.innerWidth >= SIDE_MENU_TRIGGER_BREAKPOINT) {
      closeSideMenu();
    }
  }, []);

  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';

  return (
    <NavBarWrapper
      customBackground={navBarBackground}
      customTextColor={navBarTextColor}
      bleed={navBarBleed}
      isScrolling={scrollPos !== 0}
      data-testid={`navbar-state-${userState}`}
    >
      <LogoArea>
        <Link
          track={[TRACK_CAT, 'Logo']}
          href="/?ref=logo"
          highlight
          data-testid="navigation-logo"
        >
          <ResponsiveLogo />
        </Link>
      </LogoArea>

      <LinksArea>
        <ul>
          <li>
            <MenuLink>
              <Link
                track={[TRACK_CAT, 'Features']}
                href="/features"
                data-testid="navigation-features"
              >
                Features
              </Link>
            </MenuLink>
          </li>

          {/* help menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-help"
                {...getMenuHandlers('help')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.help && (
                      <PopoverNavigation arrow={PopoverNavigation.top}>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-guide"
                            track={[TRACK_CAT, 'Guide']}
                            href="/guide"
                            component={MenuCategoryLink}
                          >
                            Guide
                          </Link>
                        </PopoverNavigationItem>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-faq"
                            track={[TRACK_CAT, 'FAQs']}
                            href="/faq"
                            component={MenuCategoryLink}
                          >
                            FAQ
                          </Link>
                        </PopoverNavigationItem>
                      </PopoverNavigation>
                    )
                  }
                >
                  <ButtonIcon>
                    <span>Help</span>
                    {menuState.help ? (
                      <IconChevronUp
                        iconSize={16}
                        color={navBarTextColor}
                        aria-label="Close help menu"
                      />
                    ) : (
                      <IconChevronDown
                        iconSize={16}
                        color={navBarTextColor}
                        aria-label="Open help menu"
                      />
                    )}
                  </ButtonIcon>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          {/* Resources menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-resources"
                {...getMenuHandlers('resources')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.resources && (
                      <PopoverNavigation arrow={PopoverNavigation.top}>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-covid"
                            track={[TRACK_CAT, 'Covid-19']}
                            href="https://directory.byspotify.com/?ref=topnav"
                            component={MenuCategoryLink}
                            prefetch={false}
                          >
                            Directory
                          </Link>
                        </PopoverNavigationItem>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-videos"
                            track={[TRACK_CAT, 'Videos']}
                            href="/videos"
                            component={MenuCategoryLink}
                          >
                            Videos
                          </Link>
                        </PopoverNavigationItem>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-blog"
                            track={[TRACK_CAT, 'Blog']}
                            href="/blog"
                            component={MenuCategoryLink}
                          >
                            News
                          </Link>
                        </PopoverNavigationItem>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-events"
                            track={[TRACK_CAT, 'Events']}
                            href="/events"
                            component={MenuCategoryLink}
                          >
                            Events
                          </Link>
                        </PopoverNavigationItem>
                        <PopoverNavigationItem>
                          <Link
                            data-testid="navigation-promocards"
                            track={[TRACK_CAT, 'Promo Cards']}
                            href="/promocards"
                            component={MenuCategoryLink}
                          >
                            Promo Cards
                          </Link>
                        </PopoverNavigationItem>
                      </PopoverNavigation>
                    )
                  }
                >
                  <ButtonIcon>
                    <span>Resources</span>
                    {menuState.resources ? (
                      <IconChevronUp
                        iconSize={16}
                        color={navBarTextColor}
                        aria-label="Close resources menu"
                      />
                    ) : (
                      <IconChevronDown
                        iconSize={16}
                        color={navBarTextColor}
                        aria-label="Open resources menu"
                      />
                    )}
                  </ButtonIcon>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          <li>
            <MenuLink>
              <Link
                track={[TRACK_CAT, 'Providers']}
                href="/directory"
                data-testid="navigation-providers"
                prefetch={false}
              >
                Providers
              </Link>
            </MenuLink>
          </li>

          {USE_SEARCH && (
            <RequireFeatureFlag
              flag="s4a-frodor-test"
              fallback={<></>}
              waiting={<></>}
            >
              <li>
                <MenuLink>
                  <Link
                    track={[TRACK_CAT, 'Search']}
                    href="/search"
                    highlight
                    data-testid="navigation-search"
                    prefetch={false}
                  >
                    <IconSearch />
                  </Link>
                </MenuLink>
              </li>
            </RequireFeatureFlag>
          )}
        </ul>
      </LinksArea>

      <ActionsArea>
        <ul>
          {showLogin && (
            <li>
              <LoginButton
                data-testid="root-login"
                highlight
                track={[TRACK_CAT, 'Log In']}
                href={`https://accounts.spotify.com/login?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                Log in
              </LoginButton>
            </li>
          )}
          {showLogout && (
            <li>
              <LoginButton
                data-testid="root-logout"
                highlight
                track={[TRACK_CAT, 'Log Out']}
                href={`https://accounts.spotify.com/logout?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                Log out
              </LoginButton>
            </li>
          )}
          {showClaim && (
            <li>
              <ClaimButton
                data-testid="navigation-claim"
                highlight
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href="/claim"
                prefetch={false}
              >
                Get access
              </ClaimButton>
            </li>
          )}
          {showDashboard && (
            <li>
              <ClaimButton
                track={[TRACK_CAT, 'Dashboard']}
                href="/c"
                highlight
                data-testid="navigation-dashboard"
                prefetch={false}
              >
                Dashboard
              </ClaimButton>
            </li>
          )}
        </ul>
      </ActionsArea>

      <MenuButtonArea>
        <OpenSideMenuButton
          onClick={() => openSideMenu()}
          data-testid="navigation-sidemenu-trigger"
        />
      </MenuButtonArea>

      {/* Nav side menu */}
      {sideMenuOpen && (
        <Modal>
          <NavSideMenu userState={userState} onClose={() => closeSideMenu()} />
          <Backdrop onClick={() => closeSideMenu()} />
        </Modal>
      )}
    </NavBarWrapper>
  );
};
